import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  INursingHomePhoenixApi,
  NursingHome,
  NursingHomeUpdate,
  TenantInformation,
} from './api';
import { NursingHomePhoenixApi } from './api';
import { getMaxReceiptBatchAmountInCents } from '@pflegenavi/shared/constants';
import type { LegalEntity } from '@pflegenavi/shared/api';
import { useAuthentication } from '@pflegenavi/shared-frontend/authentication';

const ApiContext = createContext<INursingHomePhoenixApi | undefined>(undefined);
const {
  useApi: useNursingHomePhoenixApi,
  ApiProvider: NursingHomePhoenixApiProvider,
} = makeApiProvider({
  name: 'NursingHomePhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new NursingHomePhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useNursingHomePhoenixApi,
  NursingHomePhoenixApiProvider,
  ApiContext as NursingHomePhoenixApiContext,
};

export type { NursingHome, NursingHomeUpdate, TenantInformation };

const LEGAL_ENTITIES_KEY: QueryKey = ['LEGAL_ENTITIES_KEY'];
const TENANT_INFORMATION_KEY: QueryKey = ['TENANT_INFORMATION_KEY'];
const NURSING_HOMES_KEY: QueryKey = ['NURSING_HOMES_KEY'];
const NURSING_HOME_KEY = (nursingHomeId: string): QueryKey => [
  'NURSING_HOME_KEY',
  nursingHomeId,
];
const UPDATE_NURSING_HOME_KEY = (nursingHomeId: string): QueryKey => [
  'UPDATE_NURSING_HOME_KEY',
  nursingHomeId,
];
const MANGOPAY_SCA_ENROLLMENT_KEY: QueryKey = ['MANGOPAY_SCA_ENROLLMENT_KEY'];

export const useGetPaymentProcessor = (): 'stripe' | 'mangopay' => {
  const auth = useAuthentication(true);
  return auth.paymentProcessor ?? 'stripe';
};

// Resolves the receipt-batch cap for the active tenant's payment processor.
// Prefer this over pairing useGetPaymentProcessor() with
// getMaxReceiptBatchAmountInCents() at each call site, so the processor->limit
// mapping lives in exactly one place.
export const useMaxReceiptBatchAmountInCents = (): number => {
  return getMaxReceiptBatchAmountInCents(useGetPaymentProcessor());
};

export const useGetTenantInformation = (): UseQueryResult<
  {
    data: TenantInformation;
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    TENANT_INFORMATION_KEY,
    (api) => {
      return api.getTenantInformation();
    }
  );
};

export const useGetNursingHomes = (
  options?: Omit<
    UseQueryOptions<
      {
        data: NursingHome[];
      },
      unknown,
      {
        data: NursingHome[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    data: NursingHome[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOMES_KEY,
    (api) => {
      return api.getNursingHomes();
    },
    options
  );
};

export const useGetNursingHomePhoenix = (
  nursingHomeId: string
): UseQueryResult<
  {
    data: NursingHome;
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOME_KEY(nursingHomeId),
    (api) => {
      return api.getNursingHome({ params: { nursingHomeId } });
    }
  );
};

export const useUpdateNursingHomePhoenix = (
  nursingHomeId: string
): UseMutationResult<NursingHome, unknown, NursingHomeUpdate> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<NursingHome, unknown, NursingHomeUpdate>({
    mutationKey: UPDATE_NURSING_HOME_KEY(nursingHomeId),
    mutationFn: (nursingHomeUpdate) =>
      api.updateNursingHome({
        params: { nursingHomeId },
        body: { data: nursingHomeUpdate },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: NURSING_HOME_KEY(nursingHomeId),
      });
    },
  });
  return result;
};

export const useGetLegalEntities = (
  options?: Omit<
    UseQueryOptions<
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      },
      unknown,
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    meta: {
      total_count: number;
    };
    data: LegalEntity[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    LEGAL_ENTITIES_KEY,
    (api) => {
      return api.getLegalEntities();
    },
    {
      ...options,
    }
  );
};

export const useMangopayScaEnrollment = (): UseMutationResult<
  {
    redirect_url: string;
  },
  unknown,
  {
    legal_entity_id: string;
    return_url: string;
  }
> => {
  const api = useNursingHomePhoenixApi();
  const result = useMutation<
    {
      redirect_url: string;
    },
    unknown,
    {
      legal_entity_id: string;
      return_url: string;
    }
  >({
    mutationKey: MANGOPAY_SCA_ENROLLMENT_KEY,
    mutationFn: ({ legal_entity_id, return_url }) =>
      api.mangopayScaEnrollment({
        params: {
          legalEntityId: legal_entity_id,
        },
        body: {
          return_url,
        },
      }),
  });
  return result;
};
